import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const MaintenanceIRVE = () => {
  return (
    <>
      <Helmet>
        <title>Maintenance IRVE - ZiExter | Hotline bornes de recharge électrique</title>
        <meta name="description" content="Confiez-nous la gestion et la maintenance de vos bornes électriques IRVE. Assistance client 24/7, supervision technique, performance garantie." />
        <link rel="canonical" href="https://ziexter.com/services/irve-maintenance-gestion" />
      </Helmet>

      <section className="page-hero bg-gradient-to-br from-secondary to-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl animate-fade-in-up">
            <nav className="flex items-center space-x-2 text-sm mb-4 text-gray-200">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-white">Services</Link>
              <span>/</span>
              <span className="text-white">Maintenance IRVE</span>
            </nav>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Maintenance & Gestion IRVE – ZiExter
            </h1>
            <p className="text-xl text-gray-100 mb-4">
              Confiez-nous la gestion et la maintenance de vos bornes électriques IRVE
            </p>
            <p className="text-lg text-gray-200">
              De l'assistance client à la supervision technique, ZiExter accompagne les installateurs de bornes de recharge pour garantir performance, sécurité et satisfaction des utilisateurs.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8 bg-accent">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/devis" className="btn btn-secondary">Découvrez nos solutions IRVE</Link>
            <Link to="/contact" className="btn btn-outline-white">Planifiez un rendez-vous avec un expert</Link>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Nos résultats en gestion IRVE</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '⚡', stat: '99%', label: 'de disponibilité des bornes sous contrat' },
              { icon: '📞', stat: '24/7', label: 'Assistance client en 3 langues' },
              { icon: '🛠️', stat: '95%', label: 'des incidents résolus dès le premier appel' },
              { icon: '🌍', stat: '+5', label: 'pays de déploiement d\'équipes' }
            ].map((item, i) => (
              <div key={i} className="card text-center hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="text-4xl mb-2">{item.icon}</div>
                <div className="text-3xl font-bold text-primary mb-2">{item.stat}</div>
                <p className="text-sm text-gray-600">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto animate-fade-in-up">
            <h2 className="section-title text-center mb-8">Bornes installées… mais mal gérées ?</h2>
            <p className="text-lg text-gray-700 mb-4">
              Les installateurs de bornes électriques IRVE font face à un double défi : assurer une disponibilité maximale des équipements et répondre aux demandes croissantes des utilisateurs. Une borne en panne ou mal supervisée = un client frustré et une perte d'image.
            </p>
            <p className="text-lg font-semibold text-primary">
              Avec ZiExter, externalisez la maintenance et la gestion de vos bornes : assistance technique, hotline, suivi des tickets, reporting et supervision proactive. Vous gardez le contrôle, nous assurons l'efficacité.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Des installateurs IRVE qui nous font confiance</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Cas client 1 – PME énergie, Marseille', problem: 'Bornes en panne la nuit, pas de support client.', solution: 'Mise en place d\'une hotline 24/7 et suivi automatisé des incidents.', result: '98% de disponibilité et clients rassurés.' },
              { title: 'Cas client 2 – Fabricant de bornes, Pays-Bas', problem: 'Difficulté à gérer les interventions de maintenance.', solution: 'Centralisation des tickets et pilotage des équipes de techniciens.', result: 'Délai moyen d\'intervention réduit de 40%.' },
              { title: 'Cas client 3 – Réseau hôtelier, France entière', problem: 'Bornes non supervisées, perte de clients insatisfaits.', solution: 'Supervision proactive et assistance utilisateurs multilingue.', result: '+30% d\'utilisation des bornes par les clients.' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }}>
                <h3 className="font-bold mb-3">{item.title}</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li><strong>Problème :</strong> {item.problem}</li>
                  <li><strong>Solution ZiExter :</strong> {item.solution}</li>
                  <li><strong>Résultat :</strong> {item.result}</li>
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">La performance électrique, garantie</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '⚡', stat: '4 000', label: 'tickets résolus chaque mois' },
              { icon: '🕒', stat: '20 sec', label: 'Temps moyen de réponse' },
              { icon: '🔧', stat: '80%', label: 'des problèmes résolus à distance' },
              { icon: '⭐', stat: '97%', label: 'Satisfaction utilisateur' }
            ].map((item, i) => (
              <div key={i} className="card text-center hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="text-3xl mb-2">{item.icon}</div>
                <div className="text-2xl font-bold text-primary mb-1">{item.stat}</div>
                <p className="text-sm text-gray-600">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Avis Clients</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { quote: 'Depuis que ZiExter gère nos bornes, nos clients n\'ont plus de mauvaises surprises. Service irréprochable.', name: 'Julien Robert', role: 'Responsable technique, PME Énergie, France' },
              { quote: 'Une vraie équipe partenaire, capable de résoudre les problèmes avant même qu\'ils n\'impactent nos clients.', name: 'Dirk Achterberg', role: 'Directeur, Fabricant IRVE, Tunisie' },
              { quote: 'Leur hotline multilingue nous permet d\'accueillir des clients étrangers sereinement. Un vrai plus !', name: 'Claire Garnier', role: 'Responsable accueil, Groupe hôtelier, France' }
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }}>
                <p className="text-gray-700 mb-4 italic">"{item.quote}"</p>
                <div className="font-semibold">{item.name}</div>
                <div className="text-sm text-gray-600">{item.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">FAQ</h2>
          </div>
          <div className="max-w-4xl mx-auto space-y-4">
            {[
              { q: 'Prenez-vous en charge uniquement la maintenance technique ?', a: 'Non, nous gérons aussi l\'assistance utilisateur, la supervision et le suivi des tickets.' },
              { q: 'Êtes-vous disponibles 24/7 ?', a: 'Oui, nos équipes assurent un support continu, même les jours fériés.' },
              { q: 'Pouvez-vous travailler avec mes techniciens locaux ?', a: 'Absolument, nous coordonnons vos équipes sur le terrain pour un suivi fluide.' },
              { q: 'Quel est votre temps moyen de réponse ?', a: 'Nous répondons en moins de 20 secondes en moyenne.' },
              { q: 'Peut-on commencer par un test sur un petit parc de bornes ?', a: 'Oui, nous proposons des solutions pilotes avant de passer à grande échelle.' }
            ].map((item, i) => (
              <details key={i} className="card cursor-pointer">
                <summary className="font-semibold p-4 flex justify-between items-center">
                  <span>Q : {item.q}</span>
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="p-4 pt-0 text-gray-600">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Offrez une recharge fiable et sans souci à vos clients
            </h2>
            <p className="text-xl text-gray-100 mb-8">
              ZiExter est le partenaire idéal des installateurs IRVE. Avec nous, vos bornes sont disponibles, vos clients satisfaits et votre réputation renforcée.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn btn-accent btn-lg">Contactez notre équipe IRVE</Link>
              <Link to="/devis" className="btn btn-outline-white btn-lg">Demandez un devis personnalisé</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default MaintenanceIRVE;
